import { JOB_STATUS_CONFIG } from '@/domains/jobs';
import type { Job } from '@/domains/jobs/components/JobCard';
import { NoTasksWarningIcon } from '@/domains/jobs/components/NoTasksWarningIcon';
import { formatMinutesCompact } from '@/shared/lib/duration-utils';
import type { JobPriority } from '@/shared/lib/priority';
import { Badge } from '@/shared/ui/badge';
import {
  Activity,
  CheckCircle,
  Clock,
  Layers,
  MapPin,
  ShieldCheck,
  Tag,
  Truck,
  Wrench,
} from 'lucide-react';
import { useRef } from 'react';
import { useDrag } from 'react-dnd';

interface PermitBadgeGroupProps {
  permitRequired?: boolean;
  reinstatementRequired?: boolean;
  permitStatusName?: string | null;
}

function PermitBadge() {
  return (
    <Badge variant="outline" className="text-orange-600 border-orange-600 gap-1 text-xs">
      <ShieldCheck className="size-3" />
      Permit
    </Badge>
  );
}

function ReinstatementBadge() {
  return (
    <Badge variant="outline" className="text-blue-600 border-blue-600 text-xs">
      Reinstatement
    </Badge>
  );
}

function PermitStatusLabel({ name }: { name: string }) {
  return <span className="text-xs text-muted-foreground line-clamp-1">{name}</span>;
}

function PermitBadgeGroup({
  permitRequired,
  reinstatementRequired,
  permitStatusName,
}: PermitBadgeGroupProps) {
  if (!permitRequired && !reinstatementRequired && !permitStatusName) return null;
  return (
    <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
      {permitRequired && <PermitBadge />}
      {reinstatementRequired && <ReinstatementBadge />}
      {permitStatusName && <PermitStatusLabel name={permitStatusName} />}
    </div>
  );
}

export interface DraggableJobCardProps {
  job: Job;
  isScheduled?: boolean;
  variant: 'minimal' | 'vibrant' | 'pastel' | 'gradient' | 'professional';
  index?: number;
  onJobClick?: (jobId: string) => void;
}

const priorityColors: Record<JobPriority, string> = {
  critical: 'text-red-700 dark:text-red-300 font-semibold',
  high: 'text-red-600 dark:text-red-400',
  medium: 'text-yellow-600 dark:text-yellow-400',
  low: 'text-blue-600 dark:text-blue-400',
  unknown: 'text-gray-500 dark:text-gray-400',
};

export function DraggableJobCard({
  job,
  isScheduled,
  variant,
  index = 0,
  onJobClick,
}: DraggableJobCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [{ isDragging }, drag] = useDrag({
    type: 'JOB',
    item: job,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drag(ref);

  const getVariantStyles = () => {
    const isEven = index % 2 === 0;

    switch (variant) {
      case 'vibrant':
        return {
          card: isEven
            ? 'bg-gradient-to-br from-blue-500 to-blue-600 border-blue-600 text-white'
            : 'bg-gradient-to-br from-purple-500 to-purple-600 border-purple-600 text-white',
          title: 'text-white font-semibold',
          text: 'text-white/90',
          icon: 'text-white/80',
          badge: isEven
            ? 'bg-blue-700/40 text-white border-blue-400/30'
            : 'bg-purple-700/40 text-white border-purple-400/30',
        };

      case 'pastel':
        return {
          card: isEven
            ? 'bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/40 dark:to-blue-900/40 border-blue-200 dark:border-blue-800'
            : 'bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950/40 dark:to-purple-900/40 border-purple-200 dark:border-purple-800',
          title: isEven
            ? 'text-blue-900 dark:text-blue-100 font-semibold'
            : 'text-purple-900 dark:text-purple-100 font-semibold',
          text: isEven
            ? 'text-blue-700 dark:text-blue-200'
            : 'text-purple-700 dark:text-purple-200',
          icon: isEven
            ? 'text-blue-600 dark:text-blue-300'
            : 'text-purple-600 dark:text-purple-300',
          badge: isEven
            ? 'bg-blue-200/50 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 border-blue-300 dark:border-blue-700'
            : 'bg-purple-200/50 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200 border-purple-300 dark:border-purple-700',
        };

      case 'gradient':
        return {
          card: isEven
            ? 'bg-gradient-to-r from-cyan-500/90 via-blue-500/90 to-indigo-500/90 border-blue-400 text-white shadow-lg'
            : 'bg-gradient-to-r from-pink-500/90 via-rose-500/90 to-red-500/90 border-rose-400 text-white shadow-lg',
          title: 'text-white font-bold drop-shadow-sm',
          text: 'text-white/95',
          icon: 'text-white/90',
          badge: 'bg-white/20 text-white border-white/30 backdrop-blur-sm',
        };

      case 'professional':
        return {
          card: isEven
            ? 'bg-slate-50 dark:bg-slate-800/50 border-slate-300 dark:border-slate-700'
            : 'bg-blue-50 dark:bg-blue-950/30 border-blue-300 dark:border-blue-800',
          title: isEven
            ? 'text-slate-900 dark:text-slate-100 font-semibold'
            : 'text-blue-900 dark:text-blue-100 font-semibold',
          text: isEven ? 'text-slate-700 dark:text-slate-300' : 'text-blue-700 dark:text-blue-300',
          icon: isEven ? 'text-slate-600 dark:text-slate-400' : 'text-blue-600 dark:text-blue-400',
          badge: isEven
            ? 'bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 border-slate-300 dark:border-slate-600'
            : 'bg-blue-200 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 border-blue-300 dark:border-blue-700',
        };

      default: // minimal
        return {
          card: 'bg-card border-border',
          title: 'text-foreground font-medium',
          text: 'text-muted-foreground',
          icon: 'text-muted-foreground',
          badge: JOB_STATUS_CONFIG[job.status].color,
        };
    }
  };

  const styles = getVariantStyles();

  const getStatusBar = () => {
    if (!isScheduled) return null;

    switch (job.status) {
      case 'in-progress':
        return 'border-l-4 border-l-orange-500 dark:border-l-orange-400';
      case 'completed':
        return 'border-l-4 border-l-green-500 dark:border-l-green-400';
      case 'urgent':
        return 'border-l-4 border-l-red-500 dark:border-l-red-400';
      default:
        return '';
    }
  };

  const getMilestones = () => {
    if (!isScheduled || !job.milestones) return null;

    const iconMap: Record<string, { icon: typeof Truck; color: string }> = {
      arrived_on_site: { icon: Truck, color: 'text-blue-600 dark:text-blue-400' },
      arrivedOnSite: { icon: Truck, color: 'text-blue-600 dark:text-blue-400' },
      departed: { icon: CheckCircle, color: 'text-green-600 dark:text-green-400' },
      abort_actioned: { icon: Activity, color: 'text-red-600 dark:text-red-400' },
      abortActioned: { icon: Activity, color: 'text-red-600 dark:text-red-400' },
    };

    const milestoneList = Object.entries(job.milestones)
      .filter(([, value]) => value)
      .map(([key]) => {
        const mapped = iconMap[key];
        const label = key
          .replace(/([A-Z])/g, ' $1')
          .replace(/_/g, ' ')
          .trim()
          .split(' ')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');
        return {
          icon: mapped?.icon ?? Wrench,
          label,
          color: mapped?.color ?? 'text-purple-600 dark:text-purple-400',
        };
      });

    return milestoneList.length > 0 ? milestoneList : null;
  };

  const statusBar = getStatusBar();
  const milestones = getMilestones();

  return (
    <div
      ref={ref}
      className={`p-3 rounded-lg hover:shadow-md transition-all cursor-move border-2 ${
        isDragging ? 'opacity-50' : ''
      } ${isScheduled && variant === 'minimal' ? 'border-blue-500/50' : ''} ${statusBar} ${styles.card}`}
      onClick={() => onJobClick?.(job.id)}
    >
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <h4 className={`text-sm line-clamp-1 ${styles.title}`}>{job.title}</h4>
          {/* Self-gates on scheduled + task-less, so no condition needed here. */}
          <NoTasksWarningIcon hasTasks={job.hasTasks} isScheduled={job.isScheduled} />
        </div>
        {job.isNew && (
          <Badge className="ml-2 flex-shrink-0 text-xs bg-orange-600 text-white border-0 animate-pulse">
            NEW
          </Badge>
        )}
      </div>

      {job.category && (
        <div className={`flex items-center gap-1.5 text-xs mb-1 ${styles.text}`}>
          <Layers className={`w-3 h-3 ${styles.icon} flex-shrink-0`} />
          <span className="line-clamp-1 font-medium">{job.category}</span>
        </div>
      )}

      <div className="space-y-1">
        {job.location && (
          <div className={`flex items-center gap-1.5 text-xs ${styles.text}`}>
            <MapPin className={`w-3 h-3 ${styles.icon} flex-shrink-0`} />
            <span className="line-clamp-1">{job.location}</span>
          </div>
        )}

        <div className={`flex items-center justify-between text-xs ${styles.text}`}>
          <div className="flex items-center gap-1.5">
            <Clock className={`w-3 h-3 ${styles.icon}`} />
            <span>{formatMinutesCompact(job.duration)}</span>
          </div>

          {job.client && (
            <div className="flex items-center gap-1.5">
              <Tag className={`w-3 h-3 ${styles.icon}`} />
              <span className="line-clamp-1">{job.client}</span>
            </div>
          )}
        </div>
      </div>

      <div
        className={`mt-1.5 text-xs font-medium ${
          variant === 'minimal' || variant === 'professional'
            ? priorityColors[job.priority]
            : styles.text
        }`}
      >
        {job.priority.charAt(0).toUpperCase() + job.priority.slice(1)} Priority
      </div>

      <PermitBadgeGroup
        permitRequired={job.permitRequired}
        reinstatementRequired={job.reinstatementRequired}
        permitStatusName={job.permitStatusName}
      />

      {milestones && milestones.length > 0 && (
        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-border/50">
          {milestones.map((milestone, idx) => (
            <div
              key={idx}
              className={`flex items-center gap-1 px-2 py-0.5 rounded ${
                variant === 'professional' ? 'bg-background/50' : 'bg-white/10'
              }`}
              title={milestone.label}
            >
              <milestone.icon className={`w-3 h-3 ${milestone.color}`} />
              <span className={`text-xs ${milestone.color}`}>{milestone.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
